import React, { ErrorInfo, ReactNode } from 'react'

import { AppContext, IAppContext } from '../context/app/AppContext'
import { ErrorSplash } from '../pages/BootstrapSplash/ErrorSplash'
import analyticsServiceSingleton from '../services/Analytics/initAnalytics'
import { BootstrapErrorType } from '../types/IBootstrapError'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  error?: Error
  /** The one silent remount of the app has been used */
  hasRetried: boolean
}

/**
 * The first render error remounts the app once (CLOS-11474: keep users
 * working after a one-off error). A repeat shows an error page with a reload
 * instead of silently rebuilding the app again. Both are reported.
 */
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static contextType = AppContext

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasRetried: false }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { hasRetried } = this.state
    analyticsServiceSingleton.trackError(error, {
      type: 'ErrorBoundary',
      recovery: hasRetried ? 'fallback' : 'remount',
      componentStack: errorInfo.componentStack
    })

    console.error('Error caught by ErrorBoundary:', error)
    console.error('Component stack:', errorInfo.componentStack)

    if (!hasRetried) {
      // Remount in a fresh render so a repeat is caught here again.
      this.setState({ error: undefined, hasRetried: true })
    }
  }

  render() {
    const { error, hasRetried } = this.state
    if (!error) {
      return this.props.children
    }
    if (!hasRetried) {
      return null
    }
    return (
      this.props.fallback ?? (
        <ErrorSplash
          bootstrapError={{
            type: BootstrapErrorType.GENERIC_ERROR,
            description: error.message
          }}
          supportContact={(this.context as IAppContext).supportContact}
          handleTryAgain={() => window.location.reload()}
          handleLogOut={() => window.location.assign('/')}
        />
      )
    )
  }
}

export default ErrorBoundary
